import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { resetPassword, getMe } from '../../services/api';
import { AuthContext } from '../../components/auth/AuthContext';
import { validatePassword, PASSWORD_POLICY_MESSAGE } from '../../utils/passwordUtils';
import { getFieldError, handleError } from '../../utils/errorUtils';
import { showSuccessToastFullScreen } from '../../utils/toastUtils';
import { identifyUser, captureEvent } from '../../services/analytics';
import { setMonitoringUser } from '../../services/monitoring';
import { startSession } from '../../utils/authUtils';
import { takeResetToken } from '../../utils/resetToken';
import AuthFooter from '../../components/auth/AuthFooter';
import PasswordField from '../../components/inputs/PasswordField/PasswordField';
import { ReactComponent as LogoImage } from '../../images/logo-full.svg';
import accountImage from '../../images/login-oil.png';
import styles from './ResetPassword.module.scss';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAccessToken, setUserId } = useContext(AuthContext);

  const [token, setToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [nextError, setNextError] = useState<string>();
  const [confirmError, setConfirmError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Prefer the token stashed at startup (the address bar was already scrubbed by
    // captureAndScrubResetToken before analytics booted); fall back to the URL for
    // direct renders/tests where that startup step didn't run.
    const t = takeResetToken() ?? new URLSearchParams(location.search).get('token');
    setToken(t);
    if (!t) {
      setInvalid(true);
    } else {
      // Belt-and-suspenders: ensure the token is gone from the address bar (no-op
      // in production where startup already scrubbed it).
      window.history.replaceState({}, '', '/reset-password');
    }
    setReady(true);
    // mount only — token is read once
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNextError(undefined); setConfirmError(undefined);
    const check = validatePassword(next);
    if (!check.ok) { setNextError(check.error); return; }
    if (next !== confirm) { setConfirmError('Passwords do not match.'); return; }
    if (!token) { setInvalid(true); return; }

    setSubmitting(true);

    // Step 1: the actual reset. Its failures are the recoverable form errors.
    let session: { accessToken: string; accessTokenExpiration: string };
    try {
      session = await resetPassword(token, next);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 403) setInvalid(true);
      else if (status === 422) setNextError(getFieldError(error, 'newPassword') ?? PASSWORD_POLICY_MESSAGE);
      else handleError(error);
      setSubmitting(false);
      return;
    }

    // Reset succeeded: the password is changed and a valid session was issued.
    setAccessToken(session.accessToken);
    startSession(session.accessToken, session.accessTokenExpiration);
    // Captured AFTER the session exists. On the ordinary signed-out reset flow
    // there is no token when this handler starts, so the mirror's own no-token
    // guard drops anything enqueued above -- and this event never reached the
    // activity log at all. PostHog was unaffected either way, which is why it
    // went unnoticed.
    captureEvent('password_reset_completed');

    // Step 2: auto-login by loading the profile — mirrors VerifyEmail's sequence.
    try {
      const me = await getMe();
      setUserId(me.id);
      localStorage.setItem('userId', me.id);
      localStorage.setItem('userFirstName', me.firstName);
      localStorage.setItem('userRole', me.role);
      identifyUser(me.id, { role: me.role });
      setMonitoringUser({ id: me.id, role: me.role });
      showSuccessToastFullScreen('Password updated.');
      navigate('/', { replace: true });
    } catch {
      // The password WAS changed, but we couldn't load the profile to finish
      // auto-login. Don't strand a half-written session (token but no userId →
      // PrivateRoute would bounce): clear it and send the user to sign in with
      // their new password. The /reset-password redirect-exclusion (api.ts) keeps
      // a getMe 401 from racing this with a hard redirect.
      localStorage.removeItem('accessToken');
      localStorage.removeItem('accessTokenExpiration');
      setAccessToken(null);
      showSuccessToastFullScreen('Password updated. Please sign in.');
      navigate('/signin', { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  if (!ready) return null;

  return (
    <div className={styles.container}>
      <div className={styles.logo}>
        <a href="https://jobbeast.org" target="_blank" rel="noopener noreferrer">
          <LogoImage className={styles.logo} />
        </a>
      </div>
      <div className={styles.formContainer}>
        {invalid ? (
          <>
            <h1>This link is invalid or has expired</h1>
            <p>Password reset links can only be used once and expire after 30 minutes.</p>
            <p><Link to="/forgot-password">Request a new link</Link></p>
          </>
        ) : (
          <>
            <h1>Choose a new password</h1>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="newPassword">New password</label>
                <PasswordField id="newPassword" name="newPassword" value={next} showChecklist
                  autoComplete="new-password" error={nextError}
                  onChange={(v) => { setNext(v); setNextError(undefined); setConfirmError(undefined); }} />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="confirmPassword">Confirm new password</label>
                <PasswordField id="confirmPassword" name="confirmPassword" value={confirm}
                  autoComplete="new-password" error={confirmError}
                  onChange={(v) => { setConfirm(v); setConfirmError(undefined); }} />
              </div>
              <div className={styles.buttonContainer}>
                <button type="submit" disabled={submitting}>
                  {submitting ? 'Resetting…' : 'Reset password'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
      <div className={styles.imageContainer}>
        <img src={accountImage} alt="The Beast applying for jobs" />
        <AuthFooter />
      </div>
    </div>
  );
};

export default ResetPassword;
